import { i18n } from "@lingui/core";
import { t } from "@lingui/macro";
import { I18nProvider } from "@lingui/react";
import * as Sentry from "@sentry/react";
import { Box, NativeBaseProvider } from "native-base";
import React, { Suspense, useState } from "react";
import { useEffect } from "react";
import { Router } from "react-router-dom";
import smoothscroll from "smoothscroll-polyfill";
import { ThemeProvider } from "styled-components/macro";

import { Button } from "@/design-system/controls/Button";
import { BasicErrorScreen } from "@/design-system/errors/BasicErrorScreen";
import { AppRouter } from "@/routes";
import history from "@/routes/history";
import { useTodaysDate } from "@/utilities/date-utils";
import Loading from "@design-system/overlay/Loading";
import { ToastProvider } from "@design-system/overlay/Toast";
import { WhooshNBStaffTheme, whooshTheme } from "@design-system/theme";
import { GLOBALSTYLES } from "@design-system/theme/globalStyleProvider";
import { NORMALIZE } from "@design-system/theme/normalizeProvider";

import { messages } from "./locales/en/messages.js";

function App() {
  /**
   * Polyfills
   */
  smoothscroll.polyfill();
  /**
   * Check and see if we have a session in local storage. If so
   * load it up.
   */
  const lang = "en";
  i18n.load(lang, messages);
  i18n.activate(lang);

  /**
   * This will force the app to refresh if "today" ever rolls over to a new day
   * TODO: Feels like jank. Probably should remove it?
   */
  const today = useTodaysDate();
  const [curToday, setToday] = useState(today);
  useEffect(() => {
    if (today !== curToday) {
      setToday(curToday);
      window.location.replace(window.location.href);
    }
  }, [today, curToday]);

  const logoutButton = (
    <Button onClick={() => (window.location.href = "/app/logout")}>
      {t`Click here to logout`}
    </Button>
  );

  return (
    <>
      <NativeBaseProvider theme={WhooshNBStaffTheme}>
        <Box height="100vh">
          <I18nProvider i18n={i18n}>
            <ThemeProvider theme={whooshTheme}>
              <Sentry.ErrorBoundary
                beforeCapture={(scope, error) => {
                  const chunkFailedMessage = /Loading chunk [\d]+ failed/;
                  if (
                    error?.message &&
                    chunkFailedMessage.test(error.message)
                  ) {
                    window.location.reload();
                  }
                }}
                fallback={({ resetError }) => (
                  <BasicErrorScreen
                    resetError={resetError}
                    logoutButton={logoutButton}
                  />
                )}
              >
                <ToastProvider>
                  <Suspense fallback={<Loading />}>
                    <Router history={history}>
                      <NORMALIZE />
                      <GLOBALSTYLES />
                      <AppRouter />
                    </Router>
                  </Suspense>
                </ToastProvider>
              </Sentry.ErrorBoundary>
            </ThemeProvider>
          </I18nProvider>
        </Box>
      </NativeBaseProvider>
    </>
  );
}

export default App;
